import { useCallback, useEffect, useRef, useState } from "react";
import {
	ContextMenu,
	type ContextMenuItem,
	type ContextMenuPosition,
} from "@/components/common/ContextMenu";
import { IconButton } from "@/components/common/IconButton";
import { StatusDot, type StatusDotTone } from "@/components/common/StatusDot";
import type { ChatIndicator } from "@/lib/chat";
import { resolveChatRange } from "@/lib/chat-list-selection";
import { formatRecentTimeOrDate } from "@/lib/format-datetime";
import { ROW_HOVER_BG, ROW_SELECTED_BG } from "@/lib/row-styles";
import { useTick } from "@/lib/useTick";

export interface ChatListItem {
	chatId: string;
	label: string;
	archived: boolean;
	/** 環境が削除された孤児チャット。アーカイブ解除できない。 */
	environmentDeleted: boolean;
	/** 一覧で示す状態 (未読は題名の太字、それ以外は丸)。既読で落ち着いているチャットは null。 */
	indicator: ChatIndicator | null;
	/** 最終更新日時 (ISO)。題名の tooltip で題名の下に出す。 */
	updatedAt: string;
}

interface ChatListProps {
	items: readonly ChatListItem[];
	activeChatId: string | null;
	onSelect: (chatId: string) => void;
	onRename: (chatId: string, currentTitle: string) => void;
	onArchive: (chatIds: string[]) => Promise<void>;
	onUnarchive: (chatIds: string[]) => Promise<void>;
	onDelete: (targets: ChatListItem[]) => void;
}

type OpenMenu = { chatId: string; targetIds: string[]; position: ContextMenuPosition };

type DotState = Exclude<ChatIndicator, "unread"> | "read";

const DOT_TONE: Record<DotState, StatusDotTone> = {
	running: "info",
	waitingUser: "warning",
	failed: "danger",
	read: "muted",
};

// 丸は色だけで意味を伝えるので、何を指しているかを tooltip で補う。
const DOT_LABEL: Record<DotState, string> = {
	running: "実行中",
	waitingUser: "操作待ち",
	failed: "エラーで終了",
	read: "既読",
};

/**
 * 実行中はアクセント、ユーザーの操作待ちは注意、エラーで終了した未読は危険の色の丸で示す。
 * それ以外の未読は丸ではなく題名の太字で示し、アクセントの丸を実行中だけに使う。
 * 既読で落ち着いたチャットは並ぶと煩いので丸を出さず、開いている 1 行だけグレーで示す。
 * 開いているチャットは既読なので、同期が返る前に未読の印が残っていても既読として扱う。
 */
function dotStateFor(indicator: ChatIndicator | null, isActive: boolean): DotState | null {
	if (indicator === null || indicator === "unread") return isActive ? "read" : null;
	return indicator;
}

export function ChatList({
	items,
	activeChatId,
	onSelect,
	onRename,
	onArchive,
	onUnarchive,
	onDelete,
}: ChatListProps) {
	const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
	const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
	const anchorChatIdRef = useRef<string | null>(null);
	// tooltip の「5 分前」が古くならないよう描き直す。
	useTick(60_000);

	const closeMenu = useCallback(() => setOpenMenu(null), []);
	const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

	const hasSelection = items.some((i) => selectedIds.has(i.chatId));

	useEffect(() => {
		if (!hasSelection) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") clearSelection();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [hasSelection, clearSelection]);

	if (items.length === 0) {
		return (
			<p
				data-testid="chat-tabs-empty"
				style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)", padding: "8px 12px" }}
			>
				チャット履歴がありません
			</p>
		);
	}

	const handleClick = (chatId: string, e: React.MouseEvent) => {
		if (e.shiftKey) {
			const range = resolveChatRange(
				items.map((i) => i.chatId),
				anchorChatIdRef.current ?? activeChatId,
				chatId,
			);
			setSelectedIds(new Set(range));
			return;
		}
		if (e.metaKey || e.ctrlKey) {
			// 未選択の状態では開いているチャットが選択されて見えるので、それを起点に足し引きする。
			const base = hasSelection
				? items.filter((i) => selectedIds.has(i.chatId)).map((i) => i.chatId)
				: items.filter((i) => i.chatId === activeChatId).map((i) => i.chatId);
			const next = new Set(base);
			if (next.has(chatId)) next.delete(chatId);
			else next.add(chatId);
			setSelectedIds(next);
			anchorChatIdRef.current = chatId;
			return;
		}
		clearSelection();
		anchorChatIdRef.current = chatId;
		onSelect(chatId);
	};

	// 選択中の行でメニューを開いたら選択全体を対象にし、選択外の行なら選択を解いてその行だけを対象にする。
	const openMenuFor = (chatId: string, position: ContextMenuPosition) => {
		const inSelection = selectedIds.has(chatId);
		if (!inSelection) clearSelection();
		const targetIds = inSelection
			? items.filter((i) => selectedIds.has(i.chatId)).map((i) => i.chatId)
			: [chatId];
		setOpenMenu({ chatId, targetIds, position });
	};

	const menuTargets = openMenu ? items.filter((i) => openMenu.targetIds.includes(i.chatId)) : [];

	return (
		<>
			<ul
				data-testid="chat-tabs"
				style={{
					listStyle: "none",
					margin: 0,
					padding: "0 12px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				{items.map((item) => {
					const isActive = item.chatId === activeChatId;
					const isHighlighted = hasSelection ? selectedIds.has(item.chatId) : isActive;
					const dotState = dotStateFor(item.indicator, isActive);
					const isHovered = hoveredChatId === item.chatId;
					const isMenuOpen = openMenu?.chatId === item.chatId;
					const showKebab = isHovered || isMenuOpen || isActive;

					return (
						<li
							key={item.chatId}
							style={{ position: "relative" }}
							onMouseEnter={() => setHoveredChatId(item.chatId)}
							onMouseLeave={() => setHoveredChatId((id) => (id === item.chatId ? null : id))}
						>
							<button
								type="button"
								className="text-list"
								data-testid={`chat-tab-${item.chatId}`}
								data-active={isActive ? "true" : "false"}
								data-selected={hasSelection && isHighlighted ? "true" : "false"}
								// 題名ではなく行に付け、tooltip を kebab より右から出す。
								data-tooltip={`${item.label}\n${formatRecentTimeOrDate(item.updatedAt)}`}
								data-tooltip-side="right"
								onMouseDown={(e) => {
									// Shift+クリックでラベル文字列がテキスト選択されるのを防ぐ。
									if (e.shiftKey) e.preventDefault();
								}}
								onClick={(e) => handleClick(item.chatId, e)}
								onContextMenu={(e) => {
									e.preventDefault();
									openMenuFor(item.chatId, { x: e.clientX, y: e.clientY });
								}}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									width: "100%",
									padding: "6px 10px",
									paddingRight: 32,
									borderRadius: "var(--radius-3)",
									border: 0,
									textAlign: "left",
									fontFamily: "inherit",
									color: "var(--fg-1)",
									background: isHighlighted
										? ROW_SELECTED_BG
										: isHovered
											? ROW_HOVER_BG
											: "transparent",
									cursor: "pointer",
								}}
							>
								{/* 丸を出さない行でも同じ幅を空け、ラベルの左端を揃える。 */}
								<span className="flex w-1.5 shrink-0 items-center">
									{dotState && (
										<StatusDot
											tone={DOT_TONE[dotState]}
											pulse={dotState === "running"}
											tooltip={DOT_LABEL[dotState]}
											state={dotState}
											testId={`chat-tab-dot-${item.chatId}`}
										/>
									)}
								</span>
								<span
									data-testid={`chat-tab-title-${item.chatId}`}
									style={{
										flex: 1,
										minWidth: 0,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										...(item.indicator === "unread" &&
											!isActive && { fontWeight: 600, color: "var(--fg-0)" }),
									}}
								>
									{item.label}
								</span>
							</button>
							{showKebab && (
								<IconButton
									icon="kebab"
									iconSize={14}
									size={20}
									data-testid={`chat-tab-kebab-${item.chatId}`}
									onClick={(e) => {
										e.stopPropagation();
										openMenuFor(item.chatId, { anchor: e.currentTarget });
									}}
									onContextMenu={(e) => e.preventDefault()}
									style={{
										position: "absolute",
										top: "50%",
										right: 6,
										transform: "translateY(-50%)",
									}}
								/>
							)}
						</li>
					);
				})}
			</ul>
			{openMenu && menuTargets.length > 0 && (
				<ContextMenu
					position={openMenu.position}
					onClose={closeMenu}
					testId="chat-item-menu"
					items={
						menuTargets.length === 1
							? singleChatMenuItems(menuTargets[0], { onRename, onArchive, onUnarchive, onDelete })
							: multiChatMenuItems(menuTargets, {
									onArchive,
									onUnarchive,
									onDelete,
									onDone: clearSelection,
								})
					}
				/>
			)}
		</>
	);
}

type ChatMenuHandlers = Pick<ChatListProps, "onRename" | "onArchive" | "onUnarchive" | "onDelete">;

function singleChatMenuItems(
	item: ChatListItem,
	{ onRename, onArchive, onUnarchive, onDelete }: ChatMenuHandlers,
): ContextMenuItem[] {
	return [
		{
			label: "名前を変更",
			testId: "chat-item-menu-rename",
			onSelect: () => onRename(item.chatId, item.label),
		},
		item.archived
			? {
					label: "アーカイブ解除",
					disabled: item.environmentDeleted,
					title: item.environmentDeleted
						? "エージェントが削除されたため、アーカイブを解除できません"
						: undefined,
					testId: "chat-item-menu-unarchive",
					onSelect: () => void onUnarchive([item.chatId]),
				}
			: {
					label: "アーカイブ",
					testId: "chat-item-menu-archive",
					onSelect: () => void onArchive([item.chatId]),
				},
		{
			label: "削除",
			destructive: true,
			testId: "chat-item-menu-delete",
			onSelect: () => onDelete([item]),
		},
	];
}

function multiChatMenuItems(
	targets: readonly ChatListItem[],
	{
		onArchive,
		onUnarchive,
		onDelete,
		onDone,
	}: Omit<ChatMenuHandlers, "onRename"> & { onDone: () => void },
): ContextMenuItem[] {
	const archivable = targets.filter((t) => !t.archived);
	const archived = targets.filter((t) => t.archived);
	const unarchivable = archived.filter((t) => !t.environmentDeleted);
	const items: ContextMenuItem[] = [];
	if (archivable.length > 0) {
		items.push({
			label: `${archivable.length} 件をアーカイブ`,
			testId: "chat-item-menu-archive",
			// 失敗時は選択を残し、そのまま再試行できるようにする。
			onSelect: () => void onArchive(archivable.map((t) => t.chatId)).then(onDone),
		});
	}
	if (archived.length > 0) {
		items.push({
			label: `${unarchivable.length} 件をアーカイブ解除`,
			disabled: unarchivable.length === 0,
			title:
				unarchivable.length < archived.length
					? "エージェントが削除されたチャットはアーカイブを解除できません"
					: undefined,
			testId: "chat-item-menu-unarchive",
			// 失敗時は選択を残し、そのまま再試行できるようにする。
			onSelect: () => void onUnarchive(unarchivable.map((t) => t.chatId)).then(onDone),
		});
	}
	items.push({
		label: `${targets.length} 件を削除`,
		destructive: true,
		testId: "chat-item-menu-delete",
		onSelect: () => {
			onDone();
			onDelete([...targets]);
		},
	});
	return items;
}
